import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  AnalyticsDashboard,
  AnalyticsEventForm,
  APIResponse,
  UseMediaAnalyticsResult,
  AnalyticsUpdateEvent,
  ConcurrentViewersEvent,
  WebSocketEvent,
} from '../types/mediaRecord';
import { WebSocketClient, createMediaAnalyticsClient } from '../services/websocketClient';

// API service functions
const analyticsApi = {
  getDashboard: async (
    mediaId: string, 
    tenantId: string, 
    params: {
      timeRange?: string;
      includeRealtime?: boolean;
      includeGeographic?: boolean;
      includeEngagement?: boolean;
      includeDevices?: boolean;
    } = {}
  ): Promise<AnalyticsDashboard> => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });

    const response = await fetch(`/api/media/${mediaId}/analytics?${searchParams}`, {
      headers: {
        'X-Tenant-ID': tenantId,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch analytics: ${response.statusText}`);
    }

    const result: APIResponse<AnalyticsDashboard> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.message || 'Failed to fetch analytics');
    }

    return result.data;
  },

  recordEvent: async (
    mediaId: string,
    tenantId: string,
    event: AnalyticsEventForm
  ): Promise<{ event_id: string }> => {
    // Temporarily disabled - analytics backend not ready
    if (import.meta.env.DEV) {
      console.log('Analytics event (disabled):', { mediaId, tenantId, event });
      return { event_id: `mock_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` };
    }

    const response = await fetch(`/api/media/${mediaId}/analytics`, {
      method: 'POST',
      headers: {
        'X-Tenant-ID': tenantId,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
    });

    if (!response.ok) {
      throw new Error(`Failed to record analytics event: ${response.statusText}`);
    }

    const result: APIResponse<{ event_id: string }> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.message || 'Failed to record analytics event');
    }

    return result.data;
  },
};

// Helper functions
const getTenantId = (): string => {
  return localStorage.getItem('tenantId') || import.meta.env.VITE_DEFAULT_TENANT_ID || 'dev-tenant-id';
};

const getAuthToken = (): string => {
  return localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || '';
};

/**
 * Custom hook for managing media analytics with real-time updates
 */
export const useMediaAnalytics = (
  mediaId: string,
  options: {
    timeRange?: string;
    includeRealtime?: boolean;
    includeGeographic?: boolean;
    includeEngagement?: boolean;
    includeDevices?: boolean;
    enableWebSocket?: boolean;
  } = {}
): UseMediaAnalyticsResult => {
  const queryClient = useQueryClient();
  const tenantId = getTenantId();
  const authToken = getAuthToken();
  const [concurrentViewers, setConcurrentViewers] = useState(0);
  const [realTimeEvents, setRealTimeEvents] = useState<AnalyticsUpdateEvent[]>([]);
  const wsClientRef = useRef<WebSocketClient | null>(null);

  const {
    timeRange = '7d',
    includeRealtime = true,
    includeGeographic = true,
    includeEngagement = true,
    includeDevices = true,
    enableWebSocket = true,
  } = options;

  // Initialize and manage WebSocket connection
  useEffect(() => {
    if (!enableWebSocket || !mediaId || !tenantId || !authToken) {
      return;
    }

    // Create WebSocket client
    const wsClient = createMediaAnalyticsClient(tenantId, authToken, mediaId);
    wsClientRef.current = wsClient;

    // Set up event handlers
    wsClient.on('analytics_update', (data: AnalyticsUpdateEvent['data']) => {
      if (data.media_id === mediaId) {
        const analyticsEvent: AnalyticsUpdateEvent = {
          type: 'analytics_update',
          timestamp: new Date().toISOString(),
          data
        };

        setRealTimeEvents(prev => 
          [...prev.slice(-49), analyticsEvent].sort(
            (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          )
        );
        
        // Invalidate analytics cache to refresh data
        queryClient.invalidateQueries({ 
          queryKey: ['mediaAnalytics', mediaId, tenantId] 
        });
      }
    });

    wsClient.on('concurrent_viewers_update', (data: ConcurrentViewersEvent['data']) => {
      if (data.media_id === mediaId) {
        setConcurrentViewers(data.concurrent_viewers);
      }
    });

    // Connect to WebSocket
    wsClient.connect().catch(error => {
      console.error('Failed to connect to WebSocket:', error);
    });

    // Cleanup on unmount
    return () => {
      wsClient.disconnect();
      wsClientRef.current = null;
    };
  }, [enableWebSocket, mediaId, tenantId, authToken, queryClient]);

  // Query for analytics dashboard data
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['mediaAnalytics', mediaId, tenantId, timeRange],
    queryFn: () => analyticsApi.getDashboard(mediaId, tenantId, {
      timeRange,
      includeRealtime,
      includeGeographic,
      includeEngagement,
      includeDevices,
    }),
    enabled: !!mediaId && !!tenantId,
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
    refetchInterval: enableWebSocket ? false : 60000, // Refetch every minute if WebSocket disabled
    retry: (failureCount, error) => {
      if (error.message.includes('404') || error.message.includes('401')) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Mutation for recording analytics events
  const recordEventMutation = useMutation({
    mutationFn: (event: AnalyticsEventForm) =>
      analyticsApi.recordEvent(mediaId, tenantId, event),
    onSuccess: (result) => {
      // Don't show toast for successful event recording (too noisy)
      console.log('Analytics event recorded:', result.event_id);
      
      // Optionally invalidate analytics cache
      queryClient.invalidateQueries({ 
        queryKey: ['mediaAnalytics', mediaId, tenantId] 
      });
    },
    onError: (error) => {
      // Only show error toast for debugging in development
      if (import.meta.env.DEV) {
        toast.error(`Failed to record event: ${error.message}`);
      }
      console.error('Failed to record analytics event:', error);
    },
  });

  const recordEvent = useCallback(async (event: AnalyticsEventForm): Promise<void> => {
    // Send via WebSocket for real-time updates if connected
    if (wsClientRef.current?.isConnected) {
      wsClientRef.current.sendAnalyticsEvent({
        mediaId,
        eventType: event.event_type,
        eventData: event,
      });
    }
    
    // Also send via REST API for persistent storage
    recordEventMutation.mutateAsync(event);
  }, [mediaId]); // Remove recordEventMutation dependency to prevent recreation

  return {
    data,
    isLoading,
    error: error as Error | null,
    recordEvent,
    concurrentViewers,
    realTimeEvents,
  };
};

/**
 * Hook for recording common analytics events with automatic session management
 */
export const useAnalyticsTracker = (mediaId: string) => {
  const { recordEvent } = useMediaAnalytics(mediaId);
  const sessionId = useRef<string>();

  // Generate session ID on mount
  useEffect(() => {
    sessionId.current = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const trackView = useCallback(() => {
    if (sessionId.current) {
      recordEvent({
        event_type: 'view',
        session_id: sessionId.current,
      });
    }
  }, [recordEvent]);

  const trackPlay = useCallback((position?: number) => {
    if (sessionId.current) {
      recordEvent({
        event_type: 'play',
        session_id: sessionId.current,
        position_ms: position,
      });
    }
  }, [recordEvent]);

  const trackPause = useCallback((position?: number, duration?: number) => {
    if (sessionId.current) {
      recordEvent({
        event_type: 'pause',
        session_id: sessionId.current,
        position_ms: position,
        duration_ms: duration,
      });
    }
  }, [recordEvent]);

  const trackComplete = useCallback((duration?: number) => {
    if (sessionId.current) {
      recordEvent({
        event_type: 'complete',
        session_id: sessionId.current,
        duration_ms: duration,
      });
    }
  }, [recordEvent]);

  const trackSeek = useCallback((fromPosition: number, toPosition: number) => {
    if (sessionId.current) {
      recordEvent({
        event_type: 'seek',
        session_id: sessionId.current,
        position_ms: toPosition,
        event_data: {
          from_position: fromPosition,
          to_position: toPosition,
        },
      });
    }
  }, [recordEvent]);

  const trackDownload = useCallback(() => {
    if (sessionId.current) {
      recordEvent({
        event_type: 'download',
        session_id: sessionId.current,
      });
    }
  }, [recordEvent]);

  const trackShare = useCallback((platform?: string) => {
    if (sessionId.current) {
      recordEvent({
        event_type: 'share',
        session_id: sessionId.current,
        event_data: {
          platform,
        },
      });
    }
  }, [recordEvent]);

  return {
    trackView,
    trackPlay,
    trackPause,
    trackComplete,
    trackSeek,
    trackDownload,
    trackShare,
    sessionId: sessionId.current,
  };
};

/**
 * Hook for bulk analytics operations
 */
export const useBulkAnalytics = () => {
  const queryClient = useQueryClient();
  const tenantId = getTenantId();

  const invalidateAllAnalytics = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['mediaAnalytics'] });
  }, [queryClient]);

  const prefetchAnalytics = useCallback((mediaId: string, timeRange: string = '7d') => {
    queryClient.prefetchQuery({
      queryKey: ['mediaAnalytics', mediaId, tenantId, timeRange],
      queryFn: () => analyticsApi.getDashboard(mediaId, tenantId, { timeRange }),
      staleTime: 30000,
    });
  }, [queryClient, tenantId]);

  return {
    invalidateAllAnalytics,
    prefetchAnalytics,
  };
};